/**
 * KAN-834: раскладка приложения менеджера (/m): содержимое во весь экран,
 * снизу вкладки "Внимание", "Мои лиды" и "Профиль" (KAN-837). Отступы под вырез и полосу жестов
 * (safe-area), крупные зоны нажатия.
 * KAN-845: на /m сам открывает экран "Начало работы", пока он не пройден.
 * KAN-862: вкладка "Доска" (канбан CRM).
 * KAN-866: сверху кабинет и кто вошёл (шапки нет, пока пользователь не загружен).
 * KAN-868: сверху каждой вкладки плашка "Настройка не закончена" (уведомления, Telegram).
 * KAN-870: над всеми плашками плашка "Доступна новая версия".
 * KAN-873: вместо плашек настройки и телефона одна строка пометок (MobileSetupChips).
 * KAN-881: вкладка "Внимание" ведёт на "/m/" (scope манифеста) и подсвечена
 * и на "/m" (старые установки): NavLink с end на "/m/" адрес "/m" не считает своим,
 * поэтому активность вкладок считается здесь.
 * KAN-925: справа в шапке переключатель вошедших аккаунтов.
 * KAN-964: вкладка "Доска" заменена вкладкой "Встречи" (/m/meetings); доска открывается по ссылкам.
 */
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bell, CalendarClock, UserRound, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AppUpdateBanner } from '@/components/common/AppUpdateBanner';
import { AccountSwitcher } from '@/components/layout/AccountSwitcher';
import { CabinetIdentity } from '@/components/layout/CabinetIdentity';
import { MobileSetupChips } from '@/components/mobile/MobileSetupChips';
import { OnboardingAutoOpen } from '@/components/mobile/onboarding/OnboardingAutoOpen';
import {
  isMobileHomePath,
  MOBILE_HOME,
  MOBILE_LEADS_PATH,
  MOBILE_MEETINGS_PATH,
  MOBILE_PROFILE_PATH,
} from '@/lib/mobileApp';

function isUnder(base: string): (pathname: string) => boolean {
  return (pathname) => pathname === base || pathname.startsWith(`${base}/`);
}

const TABS = [
  { to: MOBILE_HOME, label: 'Внимание', icon: Bell, isActive: isMobileHomePath },
  { to: MOBILE_LEADS_PATH, label: 'Мои лиды', icon: Users, isActive: isUnder(MOBILE_LEADS_PATH) },
  { to: MOBILE_MEETINGS_PATH, label: 'Встречи', icon: CalendarClock, isActive: isUnder(MOBILE_MEETINGS_PATH) },
  { to: MOBILE_PROFILE_PATH, label: 'Профиль', icon: UserRound, isActive: isUnder(MOBILE_PROFILE_PATH) },
] as const;

export function MobileLayout(): JSX.Element {
  const { pathname } = useLocation();
  const atRoot = isMobileHomePath(pathname);
  return (
    <div
      className="min-h-screen bg-gray-50 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]"
      data-testid="mobile-layout"
    >
      {/* empty:hidden: пока пользователь не загружен и аккаунта нет, пустой полосы нет */}
      <div className="mx-auto flex max-w-xl items-center gap-2 px-4 pt-2 empty:hidden">
        <CabinetIdentity variant="compact" as="header" className="min-w-0 flex-1" />
        <AccountSwitcher variant="mobile" className="ml-auto" />
      </div>
      <main className="mx-auto max-w-xl pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
        {atRoot && <OnboardingAutoOpen />}
        <AppUpdateBanner />
        <MobileSetupChips />
        <Outlet />
      </main>
      <nav
        aria-label="Разделы"
        className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="mx-auto flex max-w-xl">
          {TABS.map(({ to, label, icon: Icon, isActive }) => {
            const active = isActive(pathname);
            return (
              <li key={to} className="flex-1">
                <Link
                  to={to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-xs font-medium',
                    active ? 'text-blue-600' : 'text-gray-500'
                  )}
                >
                  <Icon className="h-6 w-6" aria-hidden="true" />
                  {label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
